import React, { useEffect, useRef, useState } from "react";
import authBackgroundVideo from "../../assets/auth-background.mp4";

const publicFallbackSrc = `${process.env.PUBLIC_URL || ""}/auth-background.mp4`;

/**
 * Auth fullscreen background: HTML5 video (best practice for looping H.264 MP4).
 *
 * Where to put the file:
 * - `src/assets/auth-background.mp4` — imported here; CRA emits a hashed URL (cache-friendly).
 * - `public/auth-background.mp4` — optional second `<source>`; use for very large files without bloating the build copy step (same filename).
 */
export default function AuthScreenLayout({ children, cardMaxWidthClass = "max-w-2xl" }) {
  const videoRef = useRef(null);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduceMotion(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (reduceMotion) return;
    const el = videoRef.current;
    if (!el) return;
    const tryPlay = () => {
      const p = el.play?.();
      if (p && typeof p.then === "function") p.catch(() => {});
    };
    el.addEventListener("loadeddata", tryPlay);
    tryPlay();
    return () => el.removeEventListener("loadeddata", tryPlay);
  }, [reduceMotion]);

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#0a0c10] font-sans text-white antialiased">
      {!reduceMotion ? (
        <video
          ref={videoRef}
          className="pointer-events-none absolute inset-0 z-0 h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden
        >
          <source src={authBackgroundVideo} type="video/mp4" />
          <source src={publicFallbackSrc} type="video/mp4" />
        </video>
      ) : null}

      <div
        className="absolute inset-0 z-[1] bg-gradient-to-b from-black/75 via-black/65 to-black/80"
        aria-hidden
      />

      <div className="relative z-10 flex min-h-[100dvh] w-full items-center justify-center px-3 py-8 sm:px-6 sm:py-10 md:px-8">
        <div
          className={`w-full ${cardMaxWidthClass} rounded-3xl border border-white/[0.08] bg-white/[0.025] px-6 py-8 shadow-[0_8px_48px_rgba(0,0,0,0.35)] backdrop-blur-2xl transition-[box-shadow,transform] duration-300 sm:px-10 sm:py-10 md:px-14 md:py-12`}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
